import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown, Scale } from "lucide-react";
import { scrollToId } from "../lib/scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1785665829087-139c7c40873b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxMb25kb24lMjBmaW5hbmNpYWwlMjBkaXN0cmljdCUyMGR1c2slMjBza3lsaW5lJTIwbW9kZXJuJTIwYXJjaGl0ZWN0dXJlJTIwbmlnaHQlMjBsaWdodHN8ZW58MHx8fHwxNzkzMzg4NjQyfDA&ixlib=rb-4.1.0&q=85";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const EASE = [0.16, 1, 0.3, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden bg-[#faf9f6]">
      <div className="absolute inset-0 -z-0 overflow-hidden">
        <motion.img
          src={HERO_IMG}
          alt="City of London financial district"
          style={{ y: bgY }}
          className="w-full h-full object-cover object-[50%_72%] scale-[1.15] opacity-[0.22]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#faf9f6]/75 via-[#faf9f6]/45 to-[#faf9f6]" />
      </div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-12 bg-[#a8824f]" />
          <p className="font-mono-alt text-[11px] sm:text-xs uppercase tracking-[0.35em] text-[#a8824f]">
            Financial Services Compliance Consulting &amp; Advisory
          </p>
        </motion.div>

        <h1 className="font-display font-semibold text-5xl sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tight text-[#1b2a4e] max-w-4xl">
          <Line delay={0.3}>Ensuring regulatory</Line>
          <Line delay={0.45}>
            <span className="italic text-[#a8824f]">adherence,</span> managing
          </Line>
          <Line delay={0.6}>compliance risk.</Line>
        </h1>

        <motion.p
          data-testid="hero-subcopy"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.95, ease: EASE }}
          className="mt-8 max-w-xl text-base sm:text-lg text-[#45506b] leading-relaxed"
        >
          ComplyWise Consult embeds compliance into culture, operations and customer outcomes —
          translating complex regulation into clear, actionable guidance for financial institutions.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.15, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollToId("#contact")}
            className="group flex items-center gap-2.5 bg-[#1b2a4e] text-white text-sm sm:text-base font-semibold px-7 py-4 rounded-full hover:bg-[#a8824f] hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 shadow-[0_12px_32px_rgba(27,42,78,0.22)] focus:outline-none focus:ring-2 focus:ring-[#a8824f]"
          >
            Request Advisory Session
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollToId("#services")}
            className="text-sm sm:text-base font-medium text-[#1b2a4e] px-7 py-4 rounded-full border border-[#1b2a4e]/25 hover:border-[#a8824f] hover:text-[#a8824f] bg-white/60 backdrop-blur-sm transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#a8824f]"
          >
            Explore Core Services
          </button>
        </motion.div>

        <motion.div
          data-testid="hero-credentials"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 1.45 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3"
        >
          {["Ghana Bar · Called 1995", "England & Wales Bar · 2008", "10+ Years · City of London"].map((c) => (
            <span key={c} className="flex items-center gap-2 font-mono-alt text-[11px] uppercase tracking-[0.22em] text-[#45506b]">
              <Scale size={13} className="text-[#a8824f]" />
              {c}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[#45506b] hover:text-[#a8824f] transition-colors"
        aria-label="Scroll to services"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
          <ChevronDown size={26} />
        </motion.div>
      </motion.button>
    </section>
  );
}
